import React, { useState } from 'react';
import { BarChart3, PieChart, TrendingUp, Target, Download, Copy, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { ProductDesignPageHeader, ProductDesignSection } from './priorities-ai-design/ProductDesignDocPrimitives';

export const InfographicDataVizStandards: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'charts' | 'graphs' | 'shapes' | 'labels' | 'colors' | 'annotations'>('charts');
  const [showCode, setShowCode] = useState(false);

  const ChartExample: React.FC<{
    title: string;
    description: string;
    chart: React.ReactNode;
    usage: string;
    code: string;
  }> = ({ title, description, chart, usage, code }) => (
    <div className="border border-[var(--rule)] bg-[var(--surface)] p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-heading font-semibold text-[var(--text-pri)]">{title}</h3>
          <p className="text-sm text-[var(--text-sec)]">{description}</p>
        </div>
        <button
          onClick={() => setShowCode(!showCode)}
          className="flex items-center gap-1 px-3 py-1 text-xs bg-[var(--surface-raised)] text-[var(--text-sec)] rounded hover:bg-[var(--hover-tint)] transition-colors"
        >
          {showCode ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
          {showCode ? 'Hide' : 'Show'} Code
        </button>
      </div>

      <div className="bg-[var(--surface-raised)] p-4 h-48 flex items-center justify-center relative overflow-hidden mb-4">
        {chart}
      </div>

      <div className="mb-4">
        <p className="text-sm font-semibold text-[var(--text-sec)] mb-2">Usage:</p>
        <p className="text-sm text-[var(--text-sec)]">{usage}</p>
      </div>

      {showCode && (
        <div className="bg-[var(--surface-raised)] p-4">
          <pre className="text-xs text-[var(--text-sec)] overflow-x-auto">
            <code>{code}</code>
          </pre>
        </div>
      )}
    </div>
  );

  const ShapeExample: React.FC<{
    title: string;
    description: string;
    shape: React.ReactNode;
    usage: string;
    dimensions: string;
  }> = ({ title, description, shape, usage, dimensions }) => (
    <div className="border border-[var(--rule)] bg-[var(--surface)] p-6">
      <div className="mb-4">
        <h3 className="text-lg font-heading font-semibold text-[var(--text-pri)]">{title}</h3>
        <p className="text-sm text-[var(--text-sec)]">{description}</p>
        <p className="text-xs text-[var(--text-ter)] mt-1">Dimensions: {dimensions}</p>
      </div>

      <div className="bg-[var(--surface-raised)] p-4 h-32 flex items-center justify-center relative overflow-hidden mb-4">
        {shape}
      </div>

      <div>
        <p className="text-sm font-semibold text-[var(--text-sec)] mb-2">Usage:</p>
        <p className="text-sm text-[var(--text-sec)]">{usage}</p>
      </div>
    </div>
  );

  const LabelExample: React.FC<{
    title: string;
    description: string;
    label: React.ReactNode;
    usage: string;
    typography: string;
  }> = ({ title, description, label, usage, typography }) => (
    <div className="border border-[var(--rule)] bg-[var(--surface)] p-6">
      <div className="mb-4">
        <h3 className="text-lg font-heading font-semibold text-[var(--text-pri)]">{title}</h3>
        <p className="text-sm text-[var(--text-sec)]">{description}</p>
        <p className="text-xs text-[var(--text-ter)] mt-1">Typography: {typography}</p>
      </div>

      <div className="bg-[var(--surface-raised)] p-4 h-24 flex items-center justify-center relative overflow-hidden mb-4">
        {label}
      </div>

      <div>
        <p className="text-sm font-semibold text-[var(--text-sec)] mb-2">Usage:</p>
        <p className="text-sm text-[var(--text-sec)]">{usage}</p>
      </div>
    </div>
  );

  const ColorExample: React.FC<{
    title: string;
    description: string;
    color: string;
    usage: string;
    hex: string;
  }> = ({ title, description, color, usage, hex }) => (
    <div className="border border-[var(--rule)] bg-[var(--surface)] p-6">
      <div className="mb-4">
        <h3 className="text-lg font-heading font-semibold text-[var(--text-pri)]">{title}</h3>
        <p className="text-sm text-[var(--text-sec)]">{description}</p>
        <p className="text-xs text-[var(--text-ter)] mt-1">Hex: {hex}</p>
      </div>

      <div className="bg-[var(--surface-raised)] p-4 h-24 flex items-center justify-center relative overflow-hidden mb-4">
        <div className={`w-16 h-16 ${color}  flex items-center justify-center text-white font-heading font-semibold`}>
          {hex}
        </div>
      </div>

      <div>
        <p className="text-sm font-semibold text-[var(--text-sec)] mb-2">Usage:</p>
        <p className="text-sm text-[var(--text-sec)]">{usage}</p>
      </div>
    </div>
  );

  const AnnotationExample: React.FC<{
    title: string;
    description: string;
    annotation: React.ReactNode;
    usage: string;
  }> = ({ title, description, annotation, usage }) => (
    <div className="border border-[var(--rule)] bg-[var(--surface)] p-6">
      <div className="mb-4">
        <h3 className="text-lg font-heading font-semibold text-[var(--text-pri)]">{title}</h3>
        <p className="text-sm text-[var(--text-sec)]">{description}</p>
      </div>

      <div className="bg-[var(--surface-raised)] p-4 h-32 flex items-center justify-center relative overflow-hidden mb-4">
        {annotation}
      </div>

      <div>
        <p className="text-sm font-semibold text-[var(--text-sec)] mb-2">Usage:</p>
        <p className="text-sm text-[var(--text-sec)]">{usage}</p>
      </div>
    </div>
  );

  return (
    <div>
      <ProductDesignPageHeader
        title="Infographic & Data Visualization"
        eyebrow="Charts · analytics"
        backTo="/design-language"
        backLabel="Design language"
        subtitle="Comprehensive standards for charts, graphs, bar shapes, label treatments, color coding, and annotation styles that ensure all data visuals speak the same language as Peak Priorities' design system."
      />

      {/* System Overview */}
      <ProductDesignSection title="Data Visualization System">
        <p className="text-[var(--text-sec)] leading-relaxed mb-6">
          Our data visualization system ensures all charts, graphs, and infographics maintain visual consistency
          while effectively communicating complex information. Every element should reinforce the brand's clarity and precision.
        </p>

        <div className="grid md:grid-cols-4 gap-4">
          <div className="border border-[var(--rule)] bg-[var(--surface-raised)] p-4">
            <h3 className="font-heading font-semibold text-[var(--text-pri)] mb-2">Clear</h3>
            <p className="text-sm text-[var(--text-sec)]">Data should be immediately understandable and actionable</p>
          </div>
          <div className="border border-[var(--rule)] bg-[var(--surface-raised)] p-4">
            <h3 className="font-heading font-semibold text-[var(--text-pri)] mb-2">Consistent</h3>
            <p className="text-sm text-[var(--text-sec)]">Use the same visual language across all data presentations</p>
          </div>
          <div className="border border-[var(--rule)] bg-[var(--surface-raised)] p-4">
            <h3 className="font-heading font-semibold text-[var(--text-pri)] mb-2">Accessible</h3>
            <p className="text-sm text-[var(--text-sec)]">Ensure all users can understand and interact with data</p>
          </div>
          <div className="border border-[var(--rule)] bg-[var(--surface-raised)] p-4">
            <h3 className="font-heading font-semibold text-[var(--text-pri)] mb-2">Branded</h3>
            <p className="text-sm text-[var(--text-sec)]">Reinforce Peak Priorities identity through visual choices</p>
          </div>
        </div>
      </ProductDesignSection>

      {/* Tab Navigation */}
      <div className="flex justify-center gap-4 flex-wrap mb-8">
        <button
          onClick={() => setActiveTab('charts')}
          className={`
            flex items-center gap-2 px-6 py-3  font-semibold transition-colors
            ${activeTab === 'charts'
              ? 'bg-[var(--slate)] text-white'
              : 'bg-[var(--surface-raised)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
            }
          `}
        >
          <BarChart3 className="h-5 w-5" />
          Charts
        </button>
        <button
          onClick={() => setActiveTab('graphs')}
          className={`
            flex items-center gap-2 px-6 py-3  font-semibold transition-colors
            ${activeTab === 'graphs'
              ? 'bg-[var(--slate)] text-white'
              : 'bg-[var(--surface-raised)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
            }
          `}
        >
          <TrendingUp className="h-5 w-5" />
          Graphs
        </button>
        <button
          onClick={() => setActiveTab('shapes')}
          className={`
            flex items-center gap-2 px-6 py-3  font-semibold transition-colors
            ${activeTab === 'shapes'
              ? 'bg-[var(--slate)] text-white'
              : 'bg-[var(--surface-raised)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
            }
          `}
        >
          <Target className="h-5 w-5" />
          Shapes
        </button>
        <button
          onClick={() => setActiveTab('labels')}
          className={`
            flex items-center gap-2 px-6 py-3  font-semibold transition-colors
            ${activeTab === 'labels'
              ? 'bg-[var(--slate)] text-white'
              : 'bg-[var(--surface-raised)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
            }
          `}
        >
          <Copy className="h-5 w-5" />
          Labels
        </button>
        <button
          onClick={() => setActiveTab('colors')}
          className={`
            flex items-center gap-2 px-6 py-3  font-semibold transition-colors
            ${activeTab === 'colors'
              ? 'bg-[var(--slate)] text-white'
              : 'bg-[var(--surface-raised)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
            }
          `}
        >
          <PieChart className="h-5 w-5" />
          Colors
        </button>
        <button
          onClick={() => setActiveTab('annotations')}
          className={`
            flex items-center gap-2 px-6 py-3  font-semibold transition-colors
            ${activeTab === 'annotations'
              ? 'bg-[var(--slate)] text-white'
              : 'bg-[var(--surface-raised)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
            }
          `}
        >
          <ArrowRight className="h-5 w-5" />
          Annotations
        </button>
      </div>

      {/* Chart Types */}
      {activeTab === 'charts' && (
        <ProductDesignSection title="Chart Types">
          <p className="text-[var(--text-sec)] mb-6">
            Standardized chart types with consistent styling, colors, and formatting for all Peak Priorities data presentations.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
              <ChartExample
                title="Bar Chart"
                description="Standard bar chart for comparing values"
                usage="Comparing categories, showing rankings, displaying survey results"
                code={`<div className="bar-chart">
  <div className="chart-container">
    <div className="bar" style={{ height: '60%', backgroundColor: '#3B82F6' }}>
      <span className="bar-label">Q1</span>
      <span className="bar-value">60%</span>
    </div>
    <div className="bar" style={{ height: '80%', backgroundColor: '#10B981' }}>
      <span className="bar-label">Q2</span>
      <span className="bar-value">80%</span>
    </div>
    <div className="bar" style={{ height: '45%', backgroundColor: '#F59E0B' }}>
      <span className="bar-label">Q3</span>
      <span className="bar-value">45%</span>
    </div>
  </div>
</div>`}
                chart={
                  <div className="flex items-end gap-4 h-full">
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-12 h-24 bg-[var(--slate)] rounded-t flex items-end justify-center pb-2">
                        <span className="text-white text-xs font-semibold">60%</span>
                      </div>
                      <span className="text-xs text-[var(--text-ter)]">Q1</span>
                    </div>
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-12 h-32 bg-sage-600 rounded-t flex items-end justify-center pb-2">
                        <span className="text-white text-xs font-semibold">80%</span>
                      </div>
                      <span className="text-xs text-[var(--text-ter)]">Q2</span>
                    </div>
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-12 h-18 bg-[var(--mustard)] rounded-t flex items-end justify-center pb-2">
                        <span className="text-white text-xs font-semibold">45%</span>
                      </div>
                      <span className="text-xs text-[var(--text-ter)]">Q3</span>
                    </div>
                  </div>
                }
              />

              <ChartExample
                title="Pie Chart"
                description="Circular chart for showing proportions"
                usage="Market share, budget allocation, survey responses"
                code={`<div className="pie-chart">
  <div className="pie-container">
    <div className="pie-segment" style={{
      background: 'conic-gradient(#3B82F6 0deg 144deg, #10B981 144deg 288deg, #F59E0B 288deg 360deg)'
    }}></div>
    <div className="pie-center">
      <span className="pie-total">100%</span>
    </div>
  </div>
</div>`}
                chart={
                  <div className="relative w-24 h-24">
                    <div className="w-24 h-24 rounded-full" style={{
                      background: 'conic-gradient(#3B82F6 0deg 144deg, #10B981 144deg 288deg, #F59E0B 288deg 360deg)'
                    }}></div>
                    <div className="absolute inset-4 bg-[var(--bg-raised)] rounded-full flex items-center justify-center">
                      <span className="text-xs font-semibold text-charcoal-900">100%</span>
                    </div>
                  </div>
                }
              />

              <ChartExample
                title="Line Chart"
                description="Trend line for showing changes over time"
                usage="Performance trends, growth metrics, time series data"
                code={`<div className="line-chart">
  <svg width="200" height="120">
    <path d="M20,100 L60,80 L100,60 L140,40 L180,20"
          stroke="#3B82F6" strokeWidth="3" fill="none"/>
    <circle cx="20" cy="100" r="4" fill="#3B82F6"/>
    <circle cx="60" cy="80" r="4" fill="#3B82F6"/>
    <circle cx="100" cy="60" r="4" fill="#3B82F6"/>
    <circle cx="140" cy="40" r="4" fill="#3B82F6"/>
    <circle cx="180" cy="20" r="4" fill="#3B82F6"/>
  </svg>
</div>`}
                chart={
                  <svg width="200" height="120" className="w-full h-full">
                    <path d="M20,100 L60,80 L100,60 L140,40 L180,20"
                          stroke="#3B82F6" strokeWidth="3" fill="none"/>
                    <circle cx="20" cy="100" r="4" fill="#3B82F6"/>
                    <circle cx="60" cy="80" r="4" fill="#3B82F6"/>
                    <circle cx="100" cy="60" r="4" fill="#3B82F6"/>
                    <circle cx="140" cy="40" r="4" fill="#3B82F6"/>
                    <circle cx="180" cy="20" r="4" fill="#3B82F6"/>
                  </svg>
                }
              />

              <ChartExample
                title="Area Chart"
                description="Filled area chart for showing volume over time"
                usage="Revenue trends, user growth, cumulative metrics"
                code={`<div className="area-chart">
  <svg width="200" height="120">
    <path d="M20,100 L60,80 L100,60 L140,40 L180,20 L180,100 L20,100"
          fill="#3B82F6" fillOpacity="0.3"/>
    <path d="M20,100 L60,80 L100,60 L140,40 L180,20"
          stroke="#3B82F6" strokeWidth="3" fill="none"/>
  </svg>
</div>`}
                chart={
                  <svg width="200" height="120" className="w-full h-full">
                    <path d="M20,100 L60,80 L100,60 L140,40 L180,20 L180,100 L20,100"
                          fill="#3B82F6" fillOpacity="0.3"/>
                    <path d="M20,100 L60,80 L100,60 L140,40 L180,20"
                          stroke="#3B82F6" strokeWidth="3" fill="none"/>
                  </svg>
                }
              />
          </div>
        </ProductDesignSection>
      )}

      {/* Graph Types */}
      {activeTab === 'graphs' && (
        <ProductDesignSection title="Graph Types">
          <p className="text-[var(--text-sec)] mb-6">
            Specialized graph types for complex data relationships and network visualizations.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
              <ChartExample
                title="Scatter Plot"
                description="Correlation analysis between two variables"
                usage="Performance vs effort, risk vs reward, correlation studies"
                code={`<div className="scatter-plot">
  <svg width="200" height="120">
    <circle cx="40" cy="80" r="4" fill="#3B82F6"/>
    <circle cx="80" cy="60" r="4" fill="#10B981"/>
    <circle cx="120" cy="40" r="4" fill="#F59E0B"/>
    <circle cx="160" cy="20" r="4" fill="#EF4444"/>
  </svg>
</div>`}
                chart={
                  <svg width="200" height="120" className="w-full h-full">
                    <circle cx="40" cy="80" r="4" fill="#3B82F6"/>
                    <circle cx="80" cy="60" r="4" fill="#10B981"/>
                    <circle cx="120" cy="40" r="4" fill="#F59E0B"/>
                    <circle cx="160" cy="20" r="4" fill="#EF4444"/>
                  </svg>
                }
              />

              <ChartExample
                title="Network Graph"
                description="Relationship mapping between entities"
                usage="Organizational charts, process flows, dependency mapping"
                code={`<div className="network-graph">
  <svg width="200" height="120">
    <line x1="50" y1="50" x2="100" y2="30" stroke="#3B82F6" strokeWidth="2"/>
    <line x1="50" y1="50" x2="100" y2="70" stroke="#3B82F6" strokeWidth="2"/>
    <circle cx="50" cy="50" r="8" fill="#3B82F6"/>
    <circle cx="100" cy="30" r="8" fill="#10B981"/>
    <circle cx="100" cy="70" r="8" fill="#F59E0B"/>
  </svg>
</div>`}
                chart={
                  <svg width="200" height="120" className="w-full h-full">
                    <line x1="50" y1="50" x2="100" y2="30" stroke="#3B82F6" strokeWidth="2"/>
                    <line x1="50" y1="50" x2="100" y2="70" stroke="#3B82F6" strokeWidth="2"/>
                    <circle cx="50" cy="50" r="8" fill="#3B82F6"/>
                    <circle cx="100" cy="30" r="8" fill="#10B981"/>
                    <circle cx="100" cy="70" r="8" fill="#F59E0B"/>
                  </svg>
                }
              />

              <ChartExample
                title="Heatmap"
                description="Color-coded matrix for pattern recognition"
                usage="Performance matrices, risk assessments, priority grids"
                code={`<div className="heatmap">
  <div className="grid grid-cols-4 gap-1">
    <div className="w-8 h-8 bg-[var(--coral)]"></div>
    <div className="w-8 h-8 bg-[var(--mustard)]"></div>
    <div className="w-8 h-8 bg-[var(--green)]"></div>
    <div className="w-8 h-8 bg-[var(--slate)]"></div>
  </div>
</div>`}
                chart={
                  <div className="grid grid-cols-4 gap-1">
                    <div className="w-8 h-8 bg-[var(--coral)]"></div>
                    <div className="w-8 h-8 bg-[var(--mustard)]"></div>
                    <div className="w-8 h-8 bg-[var(--green)]"></div>
                    <div className="w-8 h-8 bg-[var(--slate)]"></div>
                  </div>
                }
              />

              <ChartExample
                title="Gantt Chart"
                description="Project timeline and dependency visualization"
                usage="Project planning, milestone tracking, resource allocation"
                code={`<div className="gantt-chart">
  <div className="gantt-container">
    <div className="gantt-bar" style={{ width: '60%', backgroundColor: '#3B82F6' }}>
      <span className="gantt-label">Phase 1</span>
    </div>
    <div className="gantt-bar" style={{ width: '40%', backgroundColor: '#10B981' }}>
      <span className="gantt-label">Phase 2</span>
    </div>
  </div>
</div>`}
                chart={
                  <div className="flex flex-col gap-2 w-full">
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-6 bg-[var(--slate)] rounded flex items-center justify-center">
                        <span className="text-white text-xs">Phase 1</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-6 bg-sage-600 rounded flex items-center justify-center">
                        <span className="text-white text-xs">Phase 2</span>
                      </div>
                    </div>
                  </div>
                }
              />
          </div>
        </ProductDesignSection>
      )}

      {/* Bar Shapes */}
      {activeTab === 'shapes' && (
        <ProductDesignSection title="Bar Shapes">
          <p className="text-[var(--text-sec)] mb-6">
            Standardized bar shapes and dimensions for consistent data visualization across all Peak Priorities materials.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
              <ShapeExample
                title="Standard Bar"
                description="Default rectangular bar for most data"
                usage="General comparisons, standard metrics, baseline data"
                dimensions="Width: 12px, Corner radius: 4px"
                shape={
                  <div className="w-12 h-16 bg-[var(--slate)] rounded flex items-end justify-center pb-2">
                    <span className="text-white text-xs font-semibold">75%</span>
                  </div>
                }
              />

              <ShapeExample
                title="Rounded Bar"
                description="Softer appearance for friendly data"
                usage="User satisfaction, positive metrics, friendly comparisons"
                dimensions="Width: 12px, Corner radius: 8px"
                shape={
                  <div className="w-12 h-16 bg-sage-600 flex items-end justify-center pb-2">
                    <span className="text-white text-xs font-semibold">85%</span>
                  </div>
                }
              />

              <ShapeExample
                title="Pill Bar"
                description="Fully rounded for modern appearance"
                usage="Modern interfaces, mobile apps, contemporary designs"
                dimensions="Width: 12px, Corner radius: 50%"
                shape={
                  <div className="w-12 h-16 bg-[var(--mustard)] rounded-full flex items-end justify-center pb-2">
                    <span className="text-white text-xs font-semibold">65%</span>
                  </div>
                }
              />

              <ShapeExample
                title="Angled Bar"
                description="Dynamic appearance for growth data"
                usage="Growth metrics, progress indicators, dynamic data"
                dimensions="Width: 12px, Angled top: 45°"
                shape={
                  <div className="w-12 h-16 bg-coral-600" style={{ clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 100%)' }}>
                    <div className="h-full flex items-end justify-center pb-2">
                      <span className="text-white text-xs font-semibold">90%</span>
                    </div>
                  </div>
                }
              />
          </div>
        </ProductDesignSection>
      )}

      {/* Label Treatments */}
      {activeTab === 'labels' && (
        <ProductDesignSection title="Label Treatments">
          <p className="text-[var(--text-sec)] mb-6">
            Consistent typography and styling for all chart labels, ensuring readability and brand consistency.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
              <LabelExample
                title="Chart Title"
                description="Main chart heading"
                usage="Primary chart identification, main data story"
                typography="Inter Bold, 18px, Charcoal-900"
                label={
                  <h3 className="text-lg font-heading font-semibold text-[var(--text-pri)]">Quarterly Performance</h3>
                }
              />

              <LabelExample
                title="Axis Label"
                description="X and Y axis labels"
                usage="Axis identification, data dimension labels"
                typography="Inter Medium, 14px, Charcoal-700"
                label={
                  <p className="text-sm font-medium text-[var(--text-sec)]">Revenue (Millions)</p>
                }
              />

              <LabelExample
                title="Data Label"
                description="Individual data point labels"
                usage="Specific values, percentages, exact numbers"
                typography="Inter Semibold, 12px, White"
                label={
                  <span className="text-xs font-semibold text-white bg-[var(--slate)] px-2 py-1 rounded">$2.4M</span>
                }
              />

              <LabelExample
                title="Legend Label"
                description="Series identification labels"
                usage="Data series names, category identification"
                typography="Inter Medium, 12px, Charcoal-600"
                label={
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-[var(--slate)] rounded"></div>
                    <span className="text-xs font-medium text-[var(--text-sec)]">Q1 2024</span>
                  </div>
                }
              />
          </div>
        </ProductDesignSection>
      )}

      {/* Color Coding */}
      {activeTab === 'colors' && (
        <div className="space-y-8">
          <ProductDesignSection title="Color Coding">
            <p className="text-[var(--text-sec)] mb-6">
              Semantic color coding system for consistent data interpretation across all Peak Priorities visualizations.
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              <ColorExample
                title="Primary Data"
                description="Main data series, primary metrics"
                usage="Primary KPIs, main performance indicators, key metrics"
                hex="#3B82F6"
                color="bg-[var(--slate)]"
              />

              <ColorExample
                title="Success/Positive"
                description="Positive outcomes, growth, achievements"
                usage="Growth metrics, positive trends, successful outcomes"
                hex="#10B981"
                color="bg-sage-600"
              />

              <ColorExample
                title="Warning/Attention"
                description="Items requiring attention, moderate risk"
                usage="Warning states, moderate risk, attention items"
                hex="#F59E0B"
                color="bg-[var(--mustard)]"
              />

              <ColorExample
                title="Danger/Critical"
                description="Critical issues, high risk, failures"
                usage="Critical alerts, high risk items, failure states"
                hex="#EF4444"
                color="bg-[var(--coral)]"
              />

              <ColorExample
                title="Neutral/Secondary"
                description="Secondary data, neutral states"
                usage="Secondary metrics, neutral data, supporting information"
                hex="#6B7280"
                color="bg-[var(--surface)]"
              />

              <ColorExample
                title="Accent/Highlight"
                description="Special emphasis, highlights"
                usage="Special emphasis, highlights, accent data"
                hex="#8B5CF6"
                color="bg-[var(--slate)]"
              />
            </div>
          </ProductDesignSection>

          {/* Color Guidelines */}
          <ProductDesignSection title="Color Guidelines">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-heading font-semibold text-[var(--text-pri)] mb-4">Semantic Usage</h3>
                <div className="space-y-4">
                  <div className="border border-[var(--rule)] bg-[var(--surface-raised)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Consistent Meaning</h4>
                    <p className="text-sm text-[var(--text-sec)]">Use the same colors for the same types of data across all charts</p>
                  </div>

                  <div className="border border-[var(--rule)] bg-[var(--surface-raised)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Accessibility</h4>
                    <p className="text-sm text-[var(--text-sec)]">Ensure sufficient contrast and consider colorblind users</p>
                  </div>

                  <div className="border border-[var(--rule)] bg-[var(--surface-raised)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Brand Alignment</h4>
                    <p className="text-sm text-[var(--text-sec)]">Use brand colors when possible, extend with semantic colors</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-heading font-semibold text-[var(--text-pri)] mb-4">Color Combinations</h3>
                <div className="space-y-4">
                  <div className="border border-[var(--rule)] bg-[var(--surface-raised)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Single Series</h4>
                    <p className="text-sm text-[var(--text-sec)]">Use primary brand color for single data series</p>
                  </div>

                  <div className="border border-[var(--rule)] bg-[var(--surface-raised)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Multiple Series</h4>
                    <p className="text-sm text-[var(--text-sec)]">Use semantic colors for multiple data series</p>
                  </div>

                  <div className="border border-[var(--rule)] bg-[var(--surface-raised)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Heatmaps</h4>
                    <p className="text-sm text-[var(--text-sec)]">Use gradient from neutral to brand colors</p>
                  </div>
                </div>
              </div>
            </div>
          </ProductDesignSection>
        </div>
      )}

      {/* Annotations */}
      {activeTab === 'annotations' && (
        <ProductDesignSection title="Annotation Styles">
          <p className="text-[var(--text-sec)] mb-6">
            Consistent annotation styles for highlighting important data points, trends, and insights.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
              <AnnotationExample
                title="Callout Arrow"
                description="Pointing to specific data points"
                usage="Highlighting specific values, drawing attention to outliers"
                annotation={
                  <div className="relative">
                    <div className="w-16 h-16 bg-[var(--slate)] rounded-full flex items-center justify-center">
                      <span className="text-white text-xs font-semibold">75%</span>
                    </div>
                    <div className="absolute -top-8 left-1/2 transform -translate-x-1/2">
                      <div className="bg-charcoal-900 text-white text-xs px-2 py-1 rounded">
                        Peak Performance
                      </div>
                      <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-charcoal-900 mx-auto"></div>
                    </div>
                  </div>
                }
              />

              <AnnotationExample
                title="Trend Line"
                description="Showing directional movement"
                usage="Trend indicators, growth patterns, directional changes"
                annotation={
                  <div className="relative">
                    <svg width="120" height="60" className="w-full h-full">
                      <path d="M10,50 L60,30 L110,10" stroke="#3B82F6" strokeWidth="2" fill="none"/>
                      <text x="115" y="8" className="text-xs fill-charcoal-900">↗ Growth</text>
                    </svg>
                  </div>
                }
              />

              <AnnotationExample
                title="Highlight Box"
                description="Emphasizing data ranges"
                usage="Important ranges, significant periods, key intervals"
                annotation={
                  <div className="relative">
                    <div className="w-20 h-12 bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] rounded flex items-center justify-center">
                      <span className="text-[var(--mustard)] text-xs font-semibold">Q2-Q3</span>
                    </div>
                    <div className="absolute -top-6 left-1/2 transform -translate-x-1/2">
                      <span className="text-xs text-[var(--text-ter)]">Key Period</span>
                    </div>
                  </div>
                }
              />

              <AnnotationExample
                title="Value Label"
                description="Direct value display"
                usage="Exact values, precise numbers, specific data points"
                annotation={
                  <div className="relative">
                    <div className="w-12 h-12 bg-sage-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs font-semibold">85%</span>
                    </div>
                    <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2">
                      <span className="text-xs text-[var(--text-ter)]">Target Met</span>
                    </div>
                  </div>
                }
              />
          </div>
        </ProductDesignSection>
      )}

      {/* Implementation Guidelines */}
      <ProductDesignSection title="Implementation Guidelines">
        <p className="text-[var(--text-sec)] max-w-3xl mb-6">
          Use these data visualization standards consistently across all Peak Priorities materials.
          Every chart, graph, and infographic should reinforce the brand's clarity and precision while effectively communicating data.
        </p>
        <div className="flex gap-4 flex-wrap">
          <button className="flex items-center gap-2 px-6 py-3 bg-[var(--slate)] text-white font-semibold hover:bg-[var(--hover-tint)] transition-colors">
            <Download className="h-5 w-5" />
            Download Standards
          </button>
          <button className="flex items-center gap-2 px-6 py-3 bg-[var(--surface)] text-[var(--text-pri)] border border-[var(--slate)] font-semibold hover:bg-[var(--hover-tint)] transition-colors">
            <Copy className="h-5 w-5" />
            Copy Templates
          </button>
        </div>
      </ProductDesignSection>
    </div>
  );
};
